import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Textarea,
  Button,
  Avatar,
  Divider,
  useToast,
  Heading,
  IconButton,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from "@chakra-ui/react";
import { DeleteIcon, EditIcon } from "@chakra-ui/icons";
import axios from "axios";
import { ChatState } from "../Context/ChatProvider";
import { StarRating } from "./StarRating";

const CommentSection = ({ vendorId }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteCommentId, setDeleteCommentId] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef();

  const toast = useToast();
  const { user } = ChatState();

  // Fetch comments
  useEffect(() => {
    fetchComments();
  }, [vendorId]);

  const fetchComments = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`/api/comments/${vendorId}`);
      setComments(data);
    } catch (error) {
      console.error("Error fetching comments:", error);
    } finally {
      setLoading(false);
    }
  };

  // Add new comment
  const handleAddComment = async () => {
    if (!user) {
      toast({
        title: "Please login to comment",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!newComment.trim()) {
      toast({
        title: "Comment cannot be empty",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("authToken");
      const config = {
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const { data } = await axios.post(
        "/api/comments",
        { vendorId, comment: newComment },
        config
      );

      setComments([data, ...comments]);
      setNewComment("");
      toast({
        title: "Comment added successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error adding comment",
        description: error.response?.data?.message || "Something went wrong",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Update comment
  const handleUpdateComment = async (commentId) => {
    if (!editingText.trim()) {
      toast({
        title: "Comment cannot be empty",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("authToken");
      const config = {
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const { data } = await axios.put(
        `/api/comments/${commentId}`,
        { comment: editingText },
        config
      );

      setComments(comments.map((c) => (c._id === commentId ? data : c)));
      setEditingCommentId(null);
      setEditingText("");
      toast({
        title: "Comment updated successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error updating comment",
        description: error.response?.data?.message || "Something went wrong",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Delete comment
  const handleDeleteComment = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem("authToken");
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      await axios.delete(`/api/comments/${deleteCommentId}`, config);

      setComments(comments.filter((c) => c._id !== deleteCommentId));
      toast({
        title: "Comment deleted successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error deleting comment",
        description: error.response?.data?.message || "Something went wrong",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
      onClose();
      setDeleteCommentId(null);
    }
  };

  const formatDate = (date) => {
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(date).toLocaleDateString(undefined, options);
  };

  const openDeleteDialog = (commentId) => {
    setDeleteCommentId(commentId);
    onOpen();
  };

  return (
    <Box width="100%" mt={8}>
      <Divider my={6} />
      <Heading fontSize="2xl" mb={4}>
        Comments
      </Heading>

      {/* Add Comment Section */}
      {user ? (
        <VStack spacing={3} mb={6} align="stretch">
          <Textarea
            placeholder="Write a comment..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            size="md"
            resize="vertical"
            minHeight="100px"
          />
          <Button
            colorScheme="orange"
            onClick={handleAddComment}
            isLoading={submitting}
            alignSelf="flex-end"
          >
            Post Comment
          </Button>
        </VStack>
      ) : (
        <Box p={4} bg="gray.50" borderRadius="md" textAlign="center" mb={6}>
          <Text color="gray.600">Please login to leave a comment</Text>
        </Box>
      )}

      {/* Comments List */}
      <VStack spacing={4} align="stretch">
        {loading ? (
          <Text textAlign="center" color="gray.500">
            Loading comments...
          </Text>
        ) : comments.length === 0 ? (
          <Text textAlign="center" color="gray.500">
            No comments yet. Be the first to comment!
          </Text>
        ) : (
          comments.map((comment) => (
            <Box
              key={comment._id}
              p={4}
              bg="gray.50"
              borderRadius="md"
              border="1px solid"
              borderColor="gray.200"
            >
              <HStack align="start" justify="space-between">
                <HStack align="start" spacing={3} flex={1}>
                  <Avatar
                    size="sm"
                    name={comment.userName || comment.userId?.name}
                    src={comment.userPic || comment.userId?.pic}
                  />
                  <VStack align="start" spacing={1} flex={1}>
                    <HStack>
                      <Text fontWeight="bold" fontSize="sm">
                        {comment.userName || comment.userId?.name}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {formatDate(comment.createdAt)}
                      </Text>
                    </HStack>
                    {comment.rating > 0 && (
                      <Box>
                        <StarRating
                          rating={comment.rating}
                          size={14}
                          showText={false}
                        />
                      </Box>
                    )}
                    {editingCommentId === comment._id ? (
                      <VStack align="stretch" width="100%" spacing={2}>
                        <Textarea
                          value={editingText}
                          onChange={(e) => setEditingText(e.target.value)}
                          size="sm"
                        />
                        <HStack>
                          <Button
                            size="sm"
                            colorScheme="blue"
                            onClick={() => handleUpdateComment(comment._id)}
                            isLoading={submitting}
                          >
                            Save
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setEditingCommentId(null);
                              setEditingText("");
                            }}
                          >
                            Cancel
                          </Button>
                        </HStack>
                      </VStack>
                    ) : (
                      <Text fontSize="sm" color="gray.700">
                        {comment.comment}
                      </Text>
                    )}
                  </VStack>
                </HStack>
                {user &&
                  user._id === comment.userId?._id &&
                  !editingCommentId && (
                    <HStack spacing={1}>
                      <IconButton
                        icon={<EditIcon />}
                        size="sm"
                        variant="ghost"
                        colorScheme="blue"
                        aria-label="Edit comment"
                        onClick={() => {
                          setEditingCommentId(comment._id);
                          setEditingText(comment.comment);
                        }}
                      />
                      <IconButton
                        icon={<DeleteIcon />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        aria-label="Delete comment"
                        onClick={() => openDeleteDialog(comment._id)}
                      />
                    </HStack>
                  )}
              </HStack>
            </Box>
          ))
        )}
      </VStack>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Comment
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure? You can't undo this action afterwards.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDeleteComment}
                ml={3}
                isLoading={submitting}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default CommentSection;
